import { InputHTMLAttributes, ReactNode } from 'react';
import { useDynamicField } from '../core/useDynamicField';
import { FormName } from '../core/formRegistry';
import {
    StyledLabel,
    HiddenInput,
    CircleIndicator,
    SquareIndicator,
    Checkmark,
    LabelText,
} from './CustomCheckbox.style';

type TCheckboxInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
    label: string | ReactNode;
    name: string;
    formName: FormName;
    field: string;
    debounce?: boolean;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    circle?: boolean;
    dataTestId?: string;
    onChange?: (checked: boolean) => void;
};

export const CustomCheckbox = ({
    className,
    label,
    required,
    circle,
    dataTestId,
    disabled,
    formName,
    field,
    name,
    onChange: customOnChange,
    ...inputProps
}: TCheckboxInputProps) => {
    const f = useDynamicField(formName, field);

    return (
        <StyledLabel className={className} data-test-id={dataTestId || 'checkbox-item'}>
            <HiddenInput
                type="checkbox"
                checked={f.value}
                onChange={(e) => {
                    if (!disabled) {
                        const newValue = e.target.checked;
                        f.onChange(newValue);
                        customOnChange?.(newValue);
                    }
                }}
                {...inputProps}
            />
            {circle ? (
                <CircleIndicator
                    $checked={f.value}
                    data-test-id={`checkbox-${name}`}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && !disabled && f.onChange(!f.value)}
                />
            ) : (
                <SquareIndicator
                    $checked={f.value}
                    $disabled={!!disabled}
                    data-test-id={`checkbox-${name}`}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && !disabled && f.onChange(!f.value)}
                >
                    {f.value && <Checkmark />}
                </SquareIndicator>
            )}
            <LabelText $required={!!required}>{label}</LabelText>
        </StyledLabel>
    );
};
